import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight, MessageCircle, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Reveal, Container } from "@/components/common/Primitives";
import { PageHero, SectionHead, Rows, Kicker, Headline, Lead, TextLink, RuleDraw, ClosingBand, Success, Field, INPUT_CLS, TEXTAREA_CLS } from "@/components/common/Editorial";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { PHONE } from "@/components/layout/Header";
import { api, errorCode } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";

/* copy (binding – memory/copy_deck_nynorsk.md §6) */
const FACTS = {
  nn: [["Innsjekk", "Frå kl. 16.00"], ["Utsjekk", "Innan kl. 11.00"], ["Parkering", "Gratis for hotellgjester"], ["Resepsjon", "Open heile døgnet"]],
  en: [["Check-in", "From 16:00"], ["Check-out", "By 11:00"], ["Parking", "Free for hotel guests"], ["Reception", "Open around the clock"]],
};

const TERMS = {
  nn: [
    ["Endring og avbestilling", "Gratis fram til kl. 18.00 dagen før ankomst, for rom bestilte på nettsida."],
    ["Seinare endring eller manglande oppmøte", "Du blir belasta for første døgnet."],
    ["Inkludert i prisen", "Frukost og mva. er inkludert i hotellromprisen."],
    ["Hytter, grupper og arrangement", "Ta kontakt for eit tilbod med pris, innhald og vilkår."],
  ],
  en: [
    ["Changes and cancellation", "Free until 18:00 the day before arrival, for rooms booked on the website."],
    ["Later changes or no-show", "The first night is charged."],
    ["Included in the price", "Breakfast and VAT are included in the hotel room rate."],
    ["Cabins, groups and events", "Contact us for an offer with price, content and terms."],
  ],
};

const TRAVEL = {
  nn: [
    { t: "Frå Bergen med båt", b: "Hardangerfjordekspressen går frå Strandkaiterminalen i Bergen til Rosendal, med stopp på Flesland kai. Turen frå Bergen tek om lag to timar. Sjå avgangar og billettar hos Rødne.", link: ["Sjå båtruter og billettar", "https://rodne.no/hardangerfjordekspressen/"] },
    { t: "Frå Bergen med bil", b: "Ei vanleg rute går via Gjermundshamn og ferja til Årsnes, vidare til Rosendal. Bruk reiseplanleggjar for aktuelle ferjeavgangar og køyretid." },
    { t: "Frå Haugesund med bil", b: "Du kan køyre via Stord og ta ferja frå Skjersholmane til Ranavik, vidare mot Rosendal. Opne vegskildringa og sjekk ferjetidene før du reiser." },
    { t: "Med buss", b: "Sjå aktuelle bussruter og samband i reiseplanleggjaren til Skyss eller Entur.", link: ["Skyss", "https://www.skyss.no/"], link2: ["Entur", "https://entur.no/"] },
  ],
  en: [
    { t: "From Bergen by boat", b: "The Hardangerfjord Express runs from Strandkaiterminalen in Bergen to Rosendal, calling at Flesland quay. The trip from Bergen takes about two hours. See departures and tickets at Rødne.", link: ["Boat timetables and tickets", "https://rodne.no/hardangerfjordekspressen/"] },
    { t: "From Bergen by car", b: "A common route goes via Gjermundshamn and the ferry to Årsnes, then on to Rosendal. Use a journey planner for current ferry departures and driving time." },
    { t: "From Haugesund by car", b: "Drive via Stord and take the ferry from Skjersholmane to Ranavik, then on towards Rosendal. Open the directions and check ferry times before you leave." },
    { t: "By bus", b: "See current bus routes and connections in the Skyss or Entur journey planners.", link: ["Skyss", "https://www.skyss.no/"], link2: ["Entur", "https://entur.no/"] },
  ],
};

export default function Practical() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  const { ask } = useConcierge();
  const s = data?.settings;
  usePageMeta(tx("Praktisk informasjon", "Practical information"), tx("Innsjekk, frukost, parkering og reisa til Rosendal Fjordhotel. Spørsmål me får ofte, bestilling og vilkår.", "Check-in, breakfast, parking and travel to Rosendal Fjordhotel. Frequently asked questions, booking and terms."));
  const [sp] = useSearchParams();
  const TOPIC_PRESETS = { hytte: tx("Ledig hytte", "Cabin availability"), tilrettelagt: tx("Tilrettelagt rom", "Accessible room"), bord: tx("Bord i Baronen", "A table at Baronen") };
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: TOPIC_PRESETS[sp.get("topic")] || "", booking_ref: "", message: "", website: "" });
  const [done, setDone] = useState(false);
  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const send = useMutation({
    mutationFn: async () => (await api.post("/inquiries", { kind: "contact", ...form, lang })).data,
    onSuccess: () => { setDone(true); toast.success(tx("Takk! Meldinga er send.", "Thank you! Your message has been sent.")); },
    onError: (err) => toast.error(errorText(errorCode(err), lang)),
  });
  const lat = s?.lat || 59.9814, lng = s?.lng || 6.01;
  const bbox = `${lng - 0.012},${lat - 0.006},${lng + 0.012},${lat + 0.006}`;
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  const facts = lang === "en" ? FACTS.en : FACTS.nn;
  const travel = lang === "en" ? TRAVEL.en : TRAVEL.nn;

  return (
    <div data-testid="practical-page">
      <PageHero
        testid="practical-hero"
        image="/images/fjord-aerial.jpg"
        position="50% 55%"
        alt={tx("Rosendal og Hardangerfjorden sett frå lufta", "Rosendal and the Hardangerfjord from the air")}
        kicker={tx("Praktisk", "Practical")}
        title={tx("Godt å vite før du kjem", "Good to know before you arrive")}
        lead={tx("Her finn du informasjon om innsjekk, frukost, parkering og reisa til hotellet. Ring oss gjerne om det er noko du lurer på.", "Here you find information about check-in, breakfast, parking and travelling to the hotel. Feel free to call us if you have any questions.")}
        height="h-[70svh] min-h-[520px] max-h-[820px]"
      />

      {/* Facts strip */}
      <Container className="pt-10 lg:pt-14" data-testid="practical-quick">
        <Reveal>
          <dl className="grid grid-cols-2 gap-x-8 gap-y-8 border-y border-border py-8 lg:grid-cols-4">
            {facts.map(([t, v]) => (
              <div key={t}>
                <dt className="label-caps text-muted-foreground">{t}</dt>
                <dd className="mt-3 text-[1.25rem] font-medium leading-snug tracking-[-0.01em] text-foreground tabular sm:text-[1.375rem]">{v}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </Container>

      {/* FAQ */}
      <section className="pt-24 lg:pt-32" data-testid="practical-faq">
        <Container className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal className="lg:sticky lg:top-28">
              <Kicker className="mb-5">{tx("Spørsmål og svar", "Questions and answers")}</Kicker>
              <Headline>{tx("Spørsmål før opphaldet", "Questions before your stay")}</Headline>
              <div className="mt-10 border-t border-border pt-6">
                <p className="text-[0.9375rem] font-medium text-foreground">{tx("Finn du ikkje svaret?", "Can't find the answer?")}</p>
                <p className="mt-1.5 text-[0.9375rem] leading-relaxed text-muted-foreground">{tx("Ring oss på 53 48 80 00, eller spør Fjordverten.", "Call us on +47 53 48 80 00, or ask Fjordverten.")}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button asChild variant="outline" className="tabular" data-testid="practical-call"><a href={PHONE.tel}><Phone className="h-4 w-4" strokeWidth={1.5} /> {tx("Ring resepsjonen", "Call reception")}</a></Button>
                  <Button onClick={() => ask(null)} data-testid="practical-ask-concierge"><MessageCircle className="h-4 w-4" strokeWidth={1.5} /> {tx("Spør Fjordverten", "Ask Fjordverten")}</Button>
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.05} className="lg:col-span-8">
            <Accordion type="single" collapsible className="border-t border-border" data-testid="practical-faq-list">
              {(data?.faq || []).map((f, i) => (
                <AccordionItem key={f.id} value={f.id} className="border-border" data-testid={`faq-${f.id}`}>
                  <AccordionTrigger className="gap-6 py-5 text-left text-[1.0625rem] font-medium text-foreground hover:no-underline sm:text-[1.125rem] [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-muted-foreground">
                    <span className="flex items-baseline gap-5">
                      <span className="index-num hidden w-6 shrink-0 text-muted-foreground tnum sm:block">{String(i + 1).padStart(2, "0")}</span>
                      <span>{tr(f.q)}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-[0.9375rem] leading-relaxed text-muted-foreground sm:pl-11 sm:text-base">{tr(f.a)}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </Container>
      </section>

      {/* Booking and terms */}
      <section id="vilkar" className="scroll-mt-28 pt-24 lg:pt-32" data-testid="practical-terms">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Kicker className="mb-5">{tx("Bestilling", "Booking")}</Kicker>
              <Headline>{tx("Bestilling og vilkår", "Booking and terms")}</Headline>
              <Lead>{tx("Vel datoane og talet på gjester for å sjå ledige rom og prisar. Frukost og mva. er inkludert i hotellromprisen. Du finn opplysningane om opphaldet og vilkåra i bestillingsstadfestinga.", "Choose your dates and number of guests to see available rooms and prices. Breakfast and VAT are included in the hotel room rate. You find the details of your stay and the terms in your booking confirmation.")}</Lead>
              <p className="mt-4 max-w-[54ch] text-[1.0625rem] leading-[1.65] text-muted-foreground">{tx("For hytter, grupper og arrangement: Ta kontakt for eit tilbod med pris, innhald og vilkår.", "For cabins, groups and events: contact us for an offer with price, content and terms.")}</p>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
                <TextLink to="/bestill" testid="practical-book-link">{tx("Sjå ledige rom", "See available rooms")}</TextLink>
                <TextLink to="/mi-bestilling" testid="practical-my-booking">{tx("Mi bestilling", "My booking")}</TextLink>
              </div>
            </Reveal>
            <Reveal delay={0.05} className="lg:col-span-7 lg:pt-2">
              <Rows rows={lang === "en" ? TERMS.en : TERMS.nn} testid="practical-terms-list" labelWidth="sm:grid-cols-[15rem_1fr]" size="lg" />
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Travel */}
      <section id="veg" className="scroll-mt-28 pt-24 lg:pt-32" data-testid="practical-directions">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <Reveal>
            <SectionHead
              kicker={tx("Reise", "Travel")}
              title={tx("Slik kjem du til Rosendal", "Getting to Rosendal")}
              lead={tx("Du finn oss i Rosendalsvegen 46, ved fjorden i Rosendal. Opne vegskildringa for ruta frå der du er.", "You find us at Rosendalsvegen 46, by the fjord in Rosendal. Open the directions for the route from where you are.")}
              action={tx("Opne vegskildring", "Open directions")}
              href={mapsUrl}
              testid="practical-directions-title"
            />
          </Reveal>
          <div className="mt-12 grid gap-10 lg:mt-16 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-6">
              <dl className="divide-y divide-border border-y border-border" data-testid="practical-travel-list">
                {travel.map((r) => (
                  <div key={r.t} className="py-5">
                    <dt className="text-[1.0625rem] font-medium text-foreground">{r.t}</dt>
                    <dd className="mt-2 max-w-[56ch] text-[0.9375rem] leading-relaxed text-muted-foreground">
                      {r.b}
                      {(r.link || r.link2) && (
                        <span className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                          {r.link && <TextLink href={r.link[1]} external className="text-sm">{r.link[0]}</TextLink>}
                          {r.link2 && <TextLink href={r.link2[1]} external className="text-sm">{r.link2[0]}</TextLink>}
                        </span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-8 space-y-1.5 text-[0.9375rem] text-foreground">
                <p>{s?.address || "Rosendalsvegen 46, 5470 Rosendal"}</p>
                <p>{tx("Telefon", "Phone")}: <a href={PHONE.tel} className="underline decoration-border underline-offset-4 hover:decoration-foreground tabular" data-testid="practical-phone">{s?.phone || PHONE.intl}</a></p>
                <p>{tx("E-post", "Email")}: <a href={`mailto:${s?.email || "booking@rosendal-fjordhotel.no"}`} className="underline decoration-border underline-offset-4 hover:decoration-foreground" data-testid="practical-email">{s?.email || "booking@rosendal-fjordhotel.no"}</a></p>
              </div>
              <Button asChild size="lg" className="mt-8" data-testid="practical-maps-link"><a href={mapsUrl} target="_blank" rel="noreferrer">{tx("Opne vegskildring", "Open directions")} <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} /></a></Button>
            </Reveal>
            <Reveal className="lg:col-span-6" delay={0.05}>
              <div className="overflow-hidden rounded-photo border border-border bg-secondary">
                <iframe title={tx("Kart", "Map")} src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`} className="h-[380px] w-full grayscale-[0.2] sm:h-[520px]" loading="lazy" data-testid="practical-map" />
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Contact */}
      <section id="kontakt" className="scroll-mt-28 py-24 lg:py-32" data-testid="practical-contact">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Kicker className="mb-5">{tx("Kontakt", "Contact")}</Kicker>
              <Headline>{tx("Ta kontakt", "Get in touch")}</Headline>
              <Lead>{tx("Lurer du på eit rom, ei bestilling eller noko ved opphaldet? Send oss ei melding eller ring 53 48 80 00. Har du allereie bestilt, oppgi gjerne bestillingsnummeret ditt.", "Wondering about a room, a booking or something about your stay? Send us a message or call +47 53 48 80 00. If you have already booked, please include your booking number.")}</Lead>
              <div className="mt-8"><TextLink to="/mi-bestilling" testid="practical-contact-my-booking">{tx("Mi bestilling", "My booking")}</TextLink></div>
            </Reveal>
            <div className="lg:col-span-7">
              {done ? (
                <Success testid="contact-success" title={tx("Takk! Meldinga er send.", "Thank you! Your message has been sent.")} body={tx("Me tek kontakt med deg.", "We will get back to you.")} />
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); send.mutate(); }} className="grid gap-6 sm:grid-cols-2" data-testid="contact-form">
                  <Field id="c-name" label={tx("Namn", "Name")}><Input id="c-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={INPUT_CLS} autoComplete="name" data-testid="contact-name" /></Field>
                  <Field id="c-email" label={tx("E-postadresse", "Email address")}><Input id="c-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={INPUT_CLS} autoComplete="email" data-testid="contact-email" /></Field>
                  <Field id="c-phone" label={tx("Telefon (valfritt)", "Phone (optional)")}><Input id="c-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={INPUT_CLS} autoComplete="tel" data-testid="contact-phone" /></Field>
                  <Field id="c-topic" label={tx("Kva gjeld det?", "What is it about?")}><Input id="c-topic" value={form.topic} onChange={(e) => set("topic", e.target.value)} className={INPUT_CLS} data-testid="contact-topic" /></Field>
                  <Field id="c-ref" label={tx("Bestillingsnummer (valfritt)", "Booking number (optional)")}><Input id="c-ref" value={form.booking_ref} onChange={(e) => set("booking_ref", e.target.value.toUpperCase().slice(0, 20))} className={`${INPUT_CLS} tabular`} data-testid="contact-booking-ref" /></Field>
                  <Field id="c-message" label={tx("Meldinga di", "Your message")} className="sm:col-span-2"><Textarea id="c-message" required rows={5} value={form.message} onChange={(e) => set("message", e.target.value.slice(0, 1400))} className={TEXTAREA_CLS} data-testid="contact-message" /></Field>
                  <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  <div className="flex flex-col gap-5 border-t border-border pt-6 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="max-w-md text-xs leading-relaxed text-muted-foreground">{tx("Me brukar opplysningane til å svare på meldinga.", "We use your details to reply to your message.")} <Link to="/praktisk#vilkar" className="underline decoration-border underline-offset-4 hover:decoration-foreground">{tx("Les personvernerklæringa", "Read the privacy policy")}</Link></p>
                    <Button type="submit" size="lg" disabled={send.isPending} data-testid="contact-submit">{send.isPending ? tx("Sender …", "Sending …") : tx("Send melding", "Send message")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </Container>
      </section>

      <ClosingBand
        testid="practical-closing"
        title={tx("Når vil du kome?", "When would you like to come?")}
        lead={tx("Vel datoane dine og sjå ledige rom. Frukost er inkludert i alle hotellrom.", "Choose your dates and see available rooms. Breakfast is included in all hotel rooms.")}
        image="/images/malmangernuten.jpg"
        position="50% 55%"
      >
        <Button asChild size="lg" variant="inverse" data-testid="practical-closing-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
        <Button asChild size="lg" variant="outline-light" className="tabular" data-testid="practical-closing-phone"><a href={PHONE.tel}><Phone className="h-4 w-4" strokeWidth={1.5} /> {tx("Ring", "Call")} {PHONE.display}</a></Button>
      </ClosingBand>
    </div>
  );
}
